/**
 * CharacterDesignerNode — V2 (Sprint 9i migration)
 *
 * Migrated to use UnifiedBottomPanel tabs:
 * Config, Refine, Variants
 *
 * Uses DesignerNodeShell for shared UI (border, image, lightbox, error, handles, toolbar).
 * Unique features: editable name/description, voice section, Quick Info Bar.
 *
 * Width: 400px. Image aspect: 16:9 (aspect-video).
 */

import React, { memo, useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Position, NodeProps, useNodes, useReactFlow } from 'reactflow'
import { toast } from 'sonner'
import { MousePointer2, Sparkles, Upload } from 'lucide-react'
import {
  applyVoiceCatalogueSelection,
  type CharacterVoiceCatalogueEntry,
} from '../../../../api/client'
import { useExecuteNodeAction } from '../../../../hooks/useExecuteNodeAction'
import { generateModeFor } from '../../../../analytics/nodeAnalytics'
import { useCanvasOptional } from '../../CanvasContext'
import { useCanvasEdges } from '../../CanvasEdgesContext'
import { useCanvasNode } from '../../../../hooks/useCanvasNode'
import { getImageModelAlias } from '../../../../constants/modelAliases'
import { dataStatus, dataStrOpt, type ReactFlowCanvasEdgeData, type ReactFlowCanvasNode, ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import {
  DesignerNodeShell,
  FloatingNodeLabel,
  NODE_TYPE_ICON,
  UnifiedBottomPanel,
  QuickInfoBar,
  CHARACTER_TABS,
  DEFAULT_TAB,
  ConfigTabContent,
  IMAGE_MODEL_OPTIONS,
  RefineTabContent,
  VariantsTabContent,
  computeVariantPanelLayout,
  ShimmerPlaceholder,
  useDesignerVariantSelection,
  useVariantImageDownload,
  useBottomPanelAutoOpen,
  useSelectSingleCanvasNode,
  resolveDesignerImagePath,
  generatedImageHistoryToJson,
  getDesignerMainImage,
  getPickedIndex,
  createHandlesFromSchema,
  type SelectedReference,
  type SelectedNodeReference,
  buildAssetReferencesFromEdges,
  buildImageReferenceEdgeUpdates,
  ReferenceSelector,
  type HistoryEntry,
  useAssetNodeGeometryTrace,
} from '../shared'
import { useFeedbackMode, type RefineRunOptions } from '../../panels/useFeedbackMode'
import { AGENT_COLORS } from '../../../../constants/agentColors'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useActiveCanvasId } from '../../../../contexts/ActiveCanvasIdContext'
import { getTypedNodeData, NODE_SCHEMAS_BY_TYPE, type ReactFlowCanvasNodeDataByType } from '../../../../generated/nodeDataTypes'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'
import { NODE_BG_SWATCH_COLORS } from '../shared/nodeBgColors'
import { useVoiceCatalogueModal } from '../shared/VoiceCatalogueModalProvider'
import { tagUploadSource } from '../../../../analytics/uploadSource'
import { deriveGroupedNodeLayout } from '../GroupNode/GroupNode'
import { useNuxTooltipEvents } from '../../../../onboarding/nuxEvents'

const characterDesignerSchema = NODE_SCHEMAS_BY_TYPE.characterDesigner
type CharacterDesignerNodeData = ReactFlowCanvasNodeDataByType['characterDesigner']

const HANDLES = createHandlesFromSchema(characterDesignerSchema, {
  inputDefaults: { position: Position.Left },
  outputDefaults: { position: Position.Bottom },
  inputs: {
    in_upstream: { position: Position.Top },
  },
  outputs: {
    out_name: { position: Position.Right, style: { top: '35%' } },
    out_voice_profile: { position: Position.Right },
  },
})

const agentColor = AGENT_COLORS.character

const CHARACTER_EMPTY_ICON_PATH =
  'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'

const descriptionStateDebug = createDebug('dw:canvas:designer-description')
const descriptionStateLog = createNadDebug('dw:canvas:designer-description')
const characterVoiceLinkLog = createNadDebug('dw:character-voice-sound-link')
const characterVoiceFooterLog = createNadDebug('dw:character-voice-footer')
const manualVoiceSourceLog = createNadDebug('dw:character-manual-voice-source')
const CHARACTER_VOICE_SOURCE_OWNER_FIELD = 'in_character_voice_source_for'

// Card width (px) and the fixed variant-grid aspect (character cards are 16:9).
const NODE_WIDTH = 400
const VARIANTS_ASPECT = '16/9'
const variantsPanelLog = createNadDebug('dw:variants-panel')

function characterVoiceSoundNodeId(characterNodeId: string): string {
  return `${characterNodeId}__voice_sound`
}

function characterVoiceGroupNodeId(characterNodeId: string): string {
  return `${characterNodeId}__voice_group`
}

function readNodeParentId(node: ReactFlowCanvasNode | undefined): string | undefined {
  const withParent = node as (ReactFlowCanvasNode & { parentId?: string; parentNode?: string }) | undefined
  return withParent?.parentId ?? withParent?.parentNode
}

function readGroupChildNodeIds(node: ReactFlowCanvasNode | undefined): string[] {
  if (node?.type !== 'nodeGroup') return []
  const childIds = getTypedNodeData(node, 'nodeGroup')?.in_child_node_ids
  return Array.isArray(childIds)
    ? childIds.filter((childId): childId is string => typeof childId === 'string' && childId.trim().length > 0)
    : []
}

function readVoiceProfileName(profile: unknown): string | undefined {
  if (profile === null || typeof profile !== 'object') return undefined
  const record = profile as {
    voice_name?: unknown
    name?: unknown
    get?: (fieldName: string) => unknown
  }
  const directName = typeof record.voice_name === 'string' ? record.voice_name : undefined
  const directFallback = typeof record.name === 'string' ? record.name : undefined
  const yMapName = typeof record.get === 'function' ? record.get('voice_name') : undefined
  const yMapFallback = typeof record.get === 'function' ? record.get('name') : undefined
  return (
    directName?.trim() ||
    directFallback?.trim() ||
    (typeof yMapName === 'string' ? yMapName.trim() : '') ||
    (typeof yMapFallback === 'string' ? yMapFallback.trim() : '') ||
    undefined
  )
}

function isCharacterVoiceSourceNode(node: ReactFlowCanvasNode | undefined): boolean {
  return isCharacterVoiceSourceNodeType(node?.type)
}

function isCharacterVoiceSourceNodeType(nodeType: string | undefined): boolean {
  return nodeType === 'soundExpert' || nodeType === 'audio'
}

function readSoundExpertVoiceSourceOwner(node: ReactFlowCanvasNode | undefined): string | undefined {
  if (node?.type !== 'soundExpert') return undefined
  const soundData = getTypedNodeData(node, 'soundExpert')
  const owner = soundData?.[CHARACTER_VOICE_SOURCE_OWNER_FIELD]
  return typeof owner === 'string' && owner.trim() ? owner : undefined
}

function isActiveCharacterVoiceSourceNode(
  characterNodeId: string,
  node: ReactFlowCanvasNode | undefined,
): boolean {
  if (node?.type === 'audio') return true
  if (node?.type !== 'soundExpert') return false
  if (node.id === characterVoiceSoundNodeId(characterNodeId)) return true
  return readSoundExpertVoiceSourceOwner(node) === characterNodeId
}

function resolveLinkedVoiceProfileFromNode(
  voiceNode: ReactFlowCanvasNode | undefined,
): unknown {
  if (!voiceNode || voiceNode.type !== 'soundExpert') return undefined
  const soundData = getTypedNodeData(voiceNode, 'soundExpert')
  return soundData?.out_voice_profile ?? soundData?.in_voice_profile
}

function readSelectedVoiceName(voiceNode: ReactFlowCanvasNode | undefined): string | undefined {
  if (!voiceNode) return undefined
  if (voiceNode.type === 'soundExpert') {
    const soundData = getTypedNodeData(voiceNode, 'soundExpert')
    return (
      soundData?.selected_voice_name?.trim()
      || readVoiceProfileName(soundData?.out_voice_profile)
      || readVoiceProfileName(soundData?.in_voice_profile)
    )
  }
  if (voiceNode.type === 'audio') {
    const audioData = getTypedNodeData(voiceNode, 'audio')
    const label = typeof voiceNode.data?.label === 'string' ? voiceNode.data.label.trim() : ''
    return (
      label
      || audioData?.in_file_name?.trim()
    )
  }
  return undefined
}

function readSelectedVoiceStatus(voiceNode: ReactFlowCanvasNode | undefined): 'ready' | 'pending' | undefined {
  if (!voiceNode) return undefined
  if (voiceNode.type === 'soundExpert') {
    const soundData = getTypedNodeData(voiceNode, 'soundExpert')
    if (soundData?.out_audio) return 'ready'
    if (soundData?.selected_voice_id || soundData?.in_voice_profile) return 'pending'
    return undefined
  }
  if (voiceNode.type === 'audio') {
    const audioData = getTypedNodeData(voiceNode, 'audio')
    return audioData?.out_audio ? 'ready' : 'pending'
  }
  return undefined
}

const CharacterDesignerNode = memo(function CharacterDesignerNode(
  props: NodeProps<CharacterDesignerNodeData>
) {
  const { t } = useTranslation('canvas')
  const { id, data, selected, dragging } = props
  const status = dataStatus(data)
  const error = dataStrOpt(data, 'error')
  const cacheKey = dataStrOpt(data, 'cacheKey')
  const progressMessage = dataStrOpt(data, 'progressMessage')
  const projectId = useProjectId()
  const canvasId = useActiveCanvasId()
  const { openVoiceCatalogueModal } = useVoiceCatalogueModal()

  const { setNodes, getNodes } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const reactFlowNodes = useNodes<ReactFlowCanvasNodeData>()
  const edges = useCanvasEdges()
  const canvas = useCanvasOptional()
  const node = useCanvasNode(id, 'characterDesigner', data)
  const { emitNuxEvent } = useNuxTooltipEvents()

  const in_name = node?.get('in_name') ?? ''
  const in_role = node?.get('in_role') ?? ''
  const in_style = node?.get('in_style')
  const in_model = node?.get('in_model') ?? 'gemini-3.1-flash-image-preview'
  const in_description = node?.get('in_description') ?? ''
  const out_image = node?.get('out_image')
  const in_bg_color = node?.get('in_bg_color')

  const isRunning = status === 'running'

  // NAD trace: prove this card's width matches its schema and that the preview
  // is 16:9 (post 1592855802471717). Filter with
  // `nad trace --feature asset-node-geometry`.
  useAssetNodeGeometryTrace({
    nodeId: id,
    nodeType: 'characterDesigner',
    componentWidth: NODE_WIDTH,
    schemaWidth: characterDesignerSchema.layout['*'].width,
    imageAspectClass: 'aspect-video',
    variantsAspect: VARIANTS_ASPECT,
  })

  // Image path resolution (shared utility)
  const imagePath = resolveDesignerImagePath(out_image)

  useEffect(() => {
    descriptionStateLog({
      event: 'designer-description:render-state',
      trace_id: `designer-description:${id}:${status}`,
      node_id: id,
      node_type: 'characterDesigner',
      status,
      description_state: isRunning ? 'shimmer' : 'textarea',
      debug_enabled: descriptionStateDebug.enabled,
    })
  }, [id, isRunning, status])

  // Bottom panel state
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)
  const [voiceMenuOpen, setVoiceMenuOpen] = useState(false)
  const [voiceSelectorOpen, setVoiceSelectorOpen] = useState(false)
  const voiceUploadInputRef = useRef<HTMLInputElement | null>(null)

  // Variants open as a dedicated panel via the quick-info bar. Voice editing
  // now belongs to linked Sound Expert nodes, so Character keeps Config/Refine.
  const tabs = useMemo(() => {
    return CHARACTER_TABS.filter((t) => t.id !== 'variants' && t.id !== 'voice')
  }, [])
  const variantsMode = activeTab === 'variants'
  const selectSingleNode = useSelectSingleCanvasNode()

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      selectSingleNode(id)
      variantsPanelLog({
        event: 'bottom-panel:open',
        trace_id: `variants-panel:${id}`,
        node_id: id,
        node_type: 'characterDesigner',
        mode: tab === 'variants' ? 'variants' : 'config',
        active_tab: tab,
      })
    },
    [id, selectSingleNode]
  )

  // History state
  const mainImageHistory = useMemo(
    () => {
      void cacheKey
      return getDesignerMainImage(out_image)
    },
    [out_image, cacheKey]
  )
  const historyEntries = useMemo(
    (): HistoryEntry[] => {
      void cacheKey
      return generatedImageHistoryToJson(mainImageHistory?.history)
    },
    [mainImageHistory, cacheKey]
  )
  const pickedIndex = getPickedIndex(mainImageHistory)
  const handleVariantDownload = useVariantImageDownload({ nodeId: id, history: historyEntries })
  const batchPending = isRunning ? ((node?.get('batch_pending') as number | undefined) ?? 0) : 0

  // Dedicated Variants panel layout: tiles match the preview width; panel sized
  // for the grid (2 cols for 16:9) without scroll, grows then scrolls beyond.
  const variantLayout = useMemo(
    () =>
      computeVariantPanelLayout(
        VARIANTS_ASPECT,
        NODE_WIDTH,
        historyEntries.length + batchPending,
      ),
    [historyEntries.length, batchPending],
  )

  // Shared auto-open behavior
  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
  })

  // Feedback mode
  const { feedbackText, setFeedbackText, handleRefine } = useFeedbackMode({
    nodeId: id,
    nodeType: 'characterDesigner',
    status,
  })

  // Shared variant selection (updates picked_index)
  const handleHistorySelect = useDesignerVariantSelection({
    nodeId: id,
    nodeType: 'characterDesigner',
    historyEntries,
    outputFieldName: 'out_image',
  })

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newDesc = e.target.value
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: newDesc } } : n
        )
      )
      node?.writeInDescription(newDesc)
    },
    [id, setNodes, node]
  )

  // Config tab: generate handler — uses useExecuteNodeAction for the
  // 1s cooldown + insufficient-sparks modal handling.
  const { runExecute, isLocked: generateLocked, cancelExecution, isCancelling } = useExecuteNodeAction({
    nodeType: 'character_designer',
    projectId,
    nodeId: id,
    isRunning,
  })
  const handleConfigGenerate = useCallback(
    (retryCount: number) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, progressMessage: undefined } } : n
        )
      )
      canvas?.updateNodeData({ id, type: 'characterDesigner' }, { progressMessage: undefined })
      runExecute(retryCount, { analytics: { tab: 'config', generationMode: generateModeFor(historyEntries.length > 0) } })
    },
    [canvas, id, runExecute, setNodes, historyEntries.length],
  )

  // Refine handler
  const onRefineSubmit = useCallback((retryCount?: number, options?: RefineRunOptions) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, progressMessage: undefined } } : n
      )
    )
    canvas?.updateNodeData({ id, type: 'characterDesigner' }, { progressMessage: undefined })
    handleRefine(retryCount, options)
  }, [canvas, handleRefine, id, setNodes])

  // Style change handler
  const handleStyleChange = useCallback(
    (styleId: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_style: styleId } } : n
        )
      )
      node?.writeInStyle(styleId)
    },
    [id, setNodes, node]
  )

  // Model change handler
  const handleModelChange = useCallback(
    (model: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_model: model } } : n
        )
      )
      node?.writeInModel(model)
    },
    [id, setNodes, node]
  )

  // Reference handling for ConfigTabContent
  const currentRefs = useMemo<SelectedReference[]>(() => {
    return buildAssetReferencesFromEdges(getNodes(), edges, id, 'in_images', 'image')
  }, [edges, getNodes, id])

  const linkedVoiceEdge = useMemo(() => {
    return edges.find((edge) => {
      if (
        edge.source !== id
        || edge.sourceHandle !== 'out_voice_profile'
        || edge.targetHandle !== 'in_voice_profile'
      ) {
        return false
      }
      const targetNode = reactFlowNodes.find((candidate) => candidate.id === edge.target)
      return isActiveCharacterVoiceSourceNode(id, targetNode)
    })
  }, [edges, id, reactFlowNodes])
  const linkedVoiceNodeId = linkedVoiceEdge?.target
  const linkedVoiceNode = useMemo(
    () => reactFlowNodes.find((candidate) => candidate.id === linkedVoiceNodeId),
    [linkedVoiceNodeId, reactFlowNodes],
  )
  const linkedVoiceProfile = useMemo(() => {
    return resolveLinkedVoiceProfileFromNode(linkedVoiceNode)
  }, [linkedVoiceNode])
  const displayVoiceName = readSelectedVoiceName(linkedVoiceNode) ?? readVoiceProfileName(linkedVoiceProfile)
  const linkedVoiceStatus = readSelectedVoiceStatus(linkedVoiceNode)
  const hasLinkedVoice = !!displayVoiceName
  useEffect(() => {
    characterVoiceFooterLog({
      event: 'character-voice-footer:render',
      trace_id: `character-voice-footer:${id}`,
      character_node_id: id,
      sound_node_id: linkedVoiceNodeId ?? null,
      state: hasLinkedVoice ? 'waveform' : 'add',
      voice_status: linkedVoiceStatus ?? null,
      character_node_status: status,
      tooltip_eligible: !hasLinkedVoice && status === 'completed',
    })
  }, [hasLinkedVoice, id, linkedVoiceNodeId, linkedVoiceStatus, status])
  const handleCatalogueVoiceApply = useCallback(
    async (voice: CharacterVoiceCatalogueEntry): Promise<void> => {
      if (!projectId) {
        throw new Error(t('soundConfigFlyover.voiceSelectionUnavailable', 'Voice selection is unavailable.'))
      }
      const result = await applyVoiceCatalogueSelection(
        projectId,
        canvasId,
        { type: 'character', node_id: id },
        voice.voice_ref,
      )
      const warnings = result.warnings ?? []
      warnings
        .filter((warning) => warning.trim().length > 0)
        .forEach((warning) => toast.warning(warning, { duration: 8000 }))
      toast.success(result.message)
      if (result.success) {
        emitNuxEvent({ type: 'voiceAdded', nodeId: id, nodeType: 'characterDesigner' })
      }
    },
    [canvasId, emitNuxEvent, id, projectId, t],
  )

  const ensureCharacterVoiceGroup = useCallback(
    (voiceNodeId: string): boolean => {
      if (!canvas) return false
      const currentNodes = canvas.getNodes()
      const characterNode = currentNodes.find((candidate) => candidate.id === id)
      const voiceNode = currentNodes.find((candidate) => candidate.id === voiceNodeId)
      if (!characterNode || !voiceNode || !isCharacterVoiceSourceNodeType(voiceNode.type)) {
        manualVoiceSourceLog({
          event: 'character-voice-source:group-skipped',
          trace_id: `character-manual-voice-source:${id}`,
          character_node_id: id,
          voice_node_id: voiceNodeId,
          reason: !characterNode ? 'missing-character' : !voiceNode ? 'missing-voice' : 'invalid-voice-type',
        })
        return false
      }

      const groupId = characterVoiceGroupNodeId(id)
      const existingGroup = currentNodes.find((candidate) => candidate.id === groupId)
      const characterParent = readNodeParentId(characterNode)
      const voiceParent = readNodeParentId(voiceNode)
      if ((characterParent && characterParent !== groupId) || (voiceParent && voiceParent !== groupId)) {
        manualVoiceSourceLog({
          event: 'character-voice-source:group-skipped',
          trace_id: `character-manual-voice-source:${id}`,
          character_node_id: id,
          voice_node_id: voiceNodeId,
          reason: 'already-parented',
          character_parent_id: characterParent ?? null,
          voice_parent_id: voiceParent ?? null,
        })
        return false
      }

      const childIds = [id, voiceNodeId]
      const staleChildIds = readGroupChildNodeIds(existingGroup).filter((childId) => !childIds.includes(childId))
      const groupLabel = `${in_name || t('characterDesignerNode.untitledCharacter')} voice`
      const groupPosition = existingGroup?.position ?? {
        x: Math.min(characterNode.position.x, voiceNode.position.x) - 32,
        y: Math.min(characterNode.position.y, voiceNode.position.y) - 48,
      }

      if (!existingGroup) {
        canvas.addNode('nodeGroup', groupPosition, {
          in_label: groupLabel,
          in_child_node_ids: childIds,
          in_layout_mode: 'vertical',
          in_collapsible: false,
        }, { id: groupId })
      }

      setNodes((nodes) =>
        deriveGroupedNodeLayout(
          nodes.map((candidate) => {
            if (candidate.id === groupId) {
              return {
                ...candidate,
                data: {
                  ...candidate.data,
                  in_label: groupLabel,
                  in_child_node_ids: childIds,
                  in_layout_mode: 'vertical',
                  in_collapsible: false,
                },
              }
            }
            if (candidate.id === id || candidate.id === voiceNodeId) {
              return { ...candidate, parentId: groupId, extent: undefined }
            }
            if (staleChildIds.includes(candidate.id) && readNodeParentId(candidate) === groupId) {
              return {
                ...candidate,
                parentId: undefined,
                extent: undefined,
                position: {
                  x: groupPosition.x + candidate.position.x,
                  y: groupPosition.y + candidate.position.y,
                },
              }
            }
            return candidate
          }),
          [groupId],
        ),
      )
      requestAnimationFrame(() => {
        setNodes((nodes) => deriveGroupedNodeLayout(nodes, [groupId]))
      })

      const writeGroup = () => {
        canvas.updateNodeData({ id: groupId, type: 'nodeGroup' }, {
          in_label: groupLabel,
          in_child_node_ids: childIds,
          in_layout_mode: 'vertical',
          in_collapsible: false,
        })
        canvas.updateNodeParent(id, groupId)
        canvas.updateNodeParent(voiceNodeId, groupId)
        for (const childId of staleChildIds) {
          const child = currentNodes.find((candidate) => candidate.id === childId)
          if (readNodeParentId(child) === groupId) {
            canvas.updateNodeParent(childId, null)
            if (child) {
              canvas.updateNodePosition(
                childId,
                groupPosition.x + child.position.x,
                groupPosition.y + child.position.y,
              )
            }
          }
        }
      }
      if (canvas.transact) {
        canvas.transact(writeGroup)
      } else {
        writeGroup()
      }

      manualVoiceSourceLog({
        event: 'character-voice-source:group-linked',
        trace_id: `character-manual-voice-source:${id}`,
        character_node_id: id,
        voice_node_id: voiceNodeId,
        group_node_id: groupId,
        voice_node_type: voiceNode.type,
      })
      return true
    },
    [canvas, id, in_name, setNodes, t],
  )

  const replaceActiveVoiceSource = useCallback(
    (voiceNodeId: string, voiceNodeType?: string): boolean => {
      if (!canvas) return false
      const nextVoiceNode = reactFlowNodes.find((candidate) => candidate.id === voiceNodeId)
      const resolvedVoiceNodeType = nextVoiceNode?.type ?? voiceNodeType
      if (!isCharacterVoiceSourceNodeType(resolvedVoiceNodeType)) return false
      const save = () => {
        const linkedToOtherCharacter = edges.some((edge) => (
          edge.target === voiceNodeId
          && edge.source !== id
          && edge.sourceHandle === 'out_voice_profile'
          && edge.targetHandle === 'in_voice_profile'
        ))
        if (linkedToOtherCharacter) {
          toast.error(t('quickInfoBar.voiceSourceOwnedByAnotherCharacter', 'This voice is already linked to another character.'))
          return false
        }
        if (resolvedVoiceNodeType === 'soundExpert') {
          const stampedOwner = canvas.getYNode?.(voiceNodeId)?.get(CHARACTER_VOICE_SOURCE_OWNER_FIELD)
          const existingOwner = readSoundExpertVoiceSourceOwner(nextVoiceNode)
            ?? (typeof stampedOwner === 'string' && stampedOwner.trim() ? stampedOwner : undefined)
          if (existingOwner && existingOwner !== id) {
            toast.error(t('quickInfoBar.voiceSourceOwnedByAnotherCharacter', 'This voice is already linked to another character.'))
            return false
          }
          canvas.updateNodeData(
            { id: voiceNodeId, type: 'soundExpert' },
            { [CHARACTER_VOICE_SOURCE_OWNER_FIELD]: id },
          )
          const updatedOwner = canvas.getYNode?.(voiceNodeId)?.get(CHARACTER_VOICE_SOURCE_OWNER_FIELD)
          if (updatedOwner !== id) {
            toast.error(t('soundConfigFlyover.voiceSelectionUnavailable', 'Voice selection is unavailable.'))
            return false
          }
        }
        for (const edge of edges) {
          if (
            edge.source === id
            && edge.sourceHandle === 'out_voice_profile'
            && edge.targetHandle === 'in_voice_profile'
            && edge.target !== voiceNodeId
          ) {
            const targetNode = reactFlowNodes.find((candidate) => candidate.id === edge.target)
            if (isActiveCharacterVoiceSourceNode(id, targetNode)) {
              canvas.replacePortEdges(edge.target, 'in_voice_profile', [])
              if (
                targetNode?.type === 'soundExpert'
                && targetNode.id !== characterVoiceSoundNodeId(id)
                && readSoundExpertVoiceSourceOwner(targetNode) === id
              ) {
                canvas.updateNodeData(
                  { id: targetNode.id, type: 'soundExpert' },
                  { [CHARACTER_VOICE_SOURCE_OWNER_FIELD]: '' },
                )
              }
            }
          }
        }
        canvas.replacePortEdges(voiceNodeId, 'in_voice_profile', [
          { source: id, sourceHandle: 'out_voice_profile' },
        ])
        return true
      }
      const didSave = save()
      if (!didSave) return false
      manualVoiceSourceLog({
        event: 'character-voice-source:linked',
        trace_id: `character-manual-voice-source:${id}`,
        character_node_id: id,
        voice_node_id: voiceNodeId,
        voice_node_type: resolvedVoiceNodeType,
      })
      emitNuxEvent({ type: 'voiceAdded', nodeId: id, nodeType: 'characterDesigner' })
      ensureCharacterVoiceGroup(voiceNodeId)
      return true
    },
    [canvas, edges, emitNuxEvent, ensureCharacterVoiceGroup, id, reactFlowNodes, t],
  )

  const openAiGenerateVoice = useCallback(() => {
    characterVoiceFooterLog({
      event: 'character-voice-footer:open-ai-generate',
      trace_id: `character-voice-footer:${id}`,
      character_node_id: id,
      voice_node_id: linkedVoiceNodeId ?? null,
      state: 'menu',
      voice_status: linkedVoiceStatus ?? null,
    })
    setVoiceMenuOpen(false)
    openVoiceCatalogueModal({
      kind: 'sound_expert',
      targetLabel: in_name || t('characterDesignerNode.untitledCharacter'),
      onApply: handleCatalogueVoiceApply,
    })
  }, [
    handleCatalogueVoiceApply,
    id,
    in_name,
    linkedVoiceNodeId,
    linkedVoiceStatus,
    openVoiceCatalogueModal,
    t,
  ])

  const createUploadedVoiceNode = useCallback(
    (file: File) => {
      if (!canvas) {
        toast.error(t('soundConfigFlyover.voiceSelectionUnavailable', 'Voice selection is unavailable.'))
        return
      }
      const characterNode = reactFlowNodes.find((candidate) => candidate.id === id)
      const position = {
        x: (characterNode?.position.x ?? 0) + NODE_WIDTH + 40,
        y: characterNode?.position.y ?? 0,
      }
      const baseName = file.name.replace(/\.[^.]+$/, '').trim() || file.name
      const label = `${in_name || baseName} voice sample`
      tagUploadSource(file, 'picker')
      const voiceNodeId = canvas.addNode('audio', position, {
        initialFile: file,
        in_file_name: file.name,
        label,
      })
      if (!replaceActiveVoiceSource(voiceNodeId, 'audio')) {
        toast.error(t('soundConfigFlyover.voiceSelectionUnavailable', 'Voice selection is unavailable.'))
        return
      }
      requestAnimationFrame(() => {
        ensureCharacterVoiceGroup(voiceNodeId)
      })
      manualVoiceSourceLog({
        event: 'character-voice-source:upload-node-created',
        trace_id: `character-manual-voice-source:${id}`,
        character_node_id: id,
        voice_node_id: voiceNodeId,
        file_name: file.name,
      })
    },
    [canvas, ensureCharacterVoiceGroup, id, in_name, reactFlowNodes, replaceActiveVoiceSource, t],
  )

  const handleVoiceUploadFileSelected = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file) {
        manualVoiceSourceLog({
          event: 'character-voice-source:upload-cancelled',
          trace_id: `character-manual-voice-source:${id}`,
          character_node_id: id,
        })
        return
      }
      createUploadedVoiceNode(file)
    },
    [createUploadedVoiceNode, id],
  )

  const openUploadVoice = useCallback(() => {
    setVoiceMenuOpen(false)
    manualVoiceSourceLog({
      event: 'character-voice-source:upload-picker-opened',
      trace_id: `character-manual-voice-source:${id}`,
      character_node_id: id,
    })
    voiceUploadInputRef.current?.click()
  }, [id])

  const openVoiceSourceSelector = useCallback(() => {
    setVoiceMenuOpen(false)
    setVoiceSelectorOpen(true)
    manualVoiceSourceLog({
      event: 'character-voice-source:canvas-selector-opened',
      trace_id: `character-manual-voice-source:${id}`,
      character_node_id: id,
    })
  }, [id])

  const handleVoiceChipClick = useCallback(
    () => {
      const nextOpen = !voiceMenuOpen
      setVoiceMenuOpen(nextOpen)
      if (nextOpen) {
        emitNuxEvent({
          type: 'controlActivated',
          controlId: 'characterVoiceSourceMenuOpened',
          anchorId: 'character-add-voice',
          nodeId: id,
          nodeType: 'characterDesigner',
        })
      }
      characterVoiceFooterLog({
        event: 'character-voice-footer:open-menu',
        trace_id: `character-voice-footer:${id}`,
        character_node_id: id,
        voice_node_id: linkedVoiceNodeId ?? null,
        state: 'menu',
        voice_status: linkedVoiceStatus ?? null,
      })
      characterVoiceLinkLog({
        event: 'character-voice-chip:open-source-menu',
        trace_id: `character-voice-sound-link:${id}`,
        character_node_id: id,
        voice_node_id: linkedVoiceNodeId ?? null,
      })
    },
    [emitNuxEvent, id, linkedVoiceNodeId, linkedVoiceStatus, voiceMenuOpen],
  )

  const handleVoiceSourceSelection = useCallback(
    (selections: SelectedNodeReference[]) => {
      const selectedVoiceNode = selections[0]
      if (!selectedVoiceNode) {
        setVoiceSelectorOpen(false)
        return
      }
      if (!replaceActiveVoiceSource(selectedVoiceNode.nodeId)) return
      setVoiceSelectorOpen(false)
      selectSingleNode(selectedVoiceNode.nodeId)
    },
    [replaceActiveVoiceSource, selectSingleNode],
  )

  const handleReferencesChange = useCallback(
    (refs: SelectedReference[]) => {
      if (canvas) {
        canvas.replacePortEdges(id, 'in_images', buildImageReferenceEdgeUpdates(refs))
      }
    },
    [id, canvas]
  )

  // Prompt change handler
  const handlePromptChange = useCallback(
    (value: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: value } } : n
        )
      )
      node?.writeInDescription(value)
    },
    [id, setNodes, node]
  )

  // Rename handler for FloatingNodeLabel
  const handleRename = useCallback(
    (newName: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_name: newName } } : n
        )
      )
      node?.writeInName(newName)
    },
    [id, setNodes, node],
  )

  const handleBgColorChange = useCallback(
    (colorKey: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_bg_color: colorKey } } : n,
        ),
      )
      node?.writeInBgColor(colorKey)
    },
    [id, setNodes, node],
  )

  // Cache bust for reference image thumbnails
  const cacheBust = cacheKey

  // Footer config summary. `getImageModelAlias` misses only for a model ID the
  // capability catalog does not know (a retired or BYOM value persisted on an
  // old node), so the option list and then a generic label back it up.
  const configSummary =
    getImageModelAlias(in_model)
    ?? IMAGE_MODEL_OPTIONS.find((o) => o.value === in_model)?.label
    ?? 'Model'

  // Panel visibility: selected AND not running AND not dragging AND panel open
  // Exception: during regeneration, auto-open to Variants (handled by useBottomPanelAutoOpen)
  const panelVisible = !!selected && !dragging && bottomPanelOpen 

  // Bottom panel (rendered via bottomPanel prop to avoid overflow-hidden clipping)
  const bottomPanel = (
    <UnifiedBottomPanel
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      isVisible={panelVisible}
      hideTabBar={variantsMode}
      // Every tab is as wide as the Variants panel. Config and Refine
          // previously fell back to `calc(100% + 120px)` — 520px against a
          // 400px node, versus 832px for Variants — which left them cramped
          // and made switching tabs resize the panel under the cursor.
          // Height still varies: the Variants height is derived from the tile
          // count and collapses to 150px when empty, which would make Config
          // SMALLER, so the other tabs keep the fixed default.
          minWidth={variantLayout.minWidth}
      contentHeight={variantsMode ? variantLayout.contentHeight : undefined}
    >
      {(tab) => {
        if (tab === 'config' && projectId) {
          return (
            <ConfigTabContent
              nodeId={id}
              nodeType="character"
              prompt={in_description}
              onPromptChange={handlePromptChange}
              references={currentRefs}
              onReferencesChange={handleReferencesChange}
              currentStyleId={in_style}
              onStyleChange={handleStyleChange}
              model={in_model}
              onModelChange={handleModelChange}
              isRunning={isRunning || generateLocked}
              onGenerate={handleConfigGenerate}
              onCancel={cancelExecution}
              isCancelling={isCancelling}
              // First character generation bills at least 2 images
              // (orchestrator: count = max(count, 2) when history is empty), so
              // the quote shows 26, not 13, at 1x — intentionally matching the
              // charge. Subsequent runs (history present) bill the chosen count.
              minCount={historyEntries.length === 0 ? 2 : 1}
            />
          )
        }
        if (tab === 'refine') {
          return (
            <RefineTabContent
              nodeId={id}
              feedbackText={feedbackText}
              onFeedbackTextChange={setFeedbackText}
              onSubmit={onRefineSubmit}
              isRunning={isRunning}
              nodeType="characterDesigner"
              selectedStyleId={in_style}
              onStyleChange={handleStyleChange}
              onModelChange={handleModelChange}
              model={in_model}
              references={currentRefs}
              onReferencesChange={handleReferencesChange}
              targetBackendType="character_designer"
annotationSourcePath={imagePath}
            />
          )
        }
        if (tab === 'variants') {
          return (
            <VariantsTabContent
              history={historyEntries}
              pickedIndex={pickedIndex}
              onSelectVariant={handleHistorySelect}
              onDownloadVariant={handleVariantDownload}
              batchPending={batchPending}
              cacheKey={cacheBust}
              projectId={projectId}
              nodeId={id}
              nodeType="characterDesigner"
              agentColor={agentColor}
              aspectRatio={VARIANTS_ASPECT}
              maxWidth={NODE_WIDTH}
            />
          )
        }
        return null
      }}
    </UnifiedBottomPanel>
  )

  return (
    <DesignerNodeShell
      nodeId={id}
      selected={selected}
      dragging={dragging}
      status={status}
      error={error}
      progressMessage={progressMessage}
      nodeName={in_name}
      agentTypeLabel={t('characterDesignerNode.characterType')}
      nodeType="characterDesigner"
      agentColor={agentColor}
      width={NODE_WIDTH}
      imageAspect="aspect-video"
      handles={HANDLES}
      testId="character-designer-node"
      imagePath={imagePath}
      cacheKey={cacheKey}
      emptyIconPath={CHARACTER_EMPTY_ICON_PATH}
      onCancel={cancelExecution}
      isCancelling={isCancelling}
      imageHistory={historyEntries}
      pickedIndex={pickedIndex}
      bottomPanel={bottomPanel}
      floatingLabel={<FloatingNodeLabel name={in_name} fallback={t('characterDesignerNode.untitledCharacter')} typeIcon={NODE_TYPE_ICON.characterDesigner} onRename={handleRename} bgColor={in_bg_color && in_bg_color !== 'default' ? NODE_BG_SWATCH_COLORS[in_bg_color] : undefined} />}
      bgColorKey={in_bg_color}
      onBgColorChange={handleBgColorChange}
    >
      {/* Content Area */}
      <div className="pt-2 px-6 pb-6">
        {/* Role label */}
        <span data-testid="character-role-label" className="text-xs font-medium" style={{ color: agentColor.active }}>
          {in_role ? in_role.charAt(0).toUpperCase() + in_role.slice(1) : 'Character'}
        </span>

        {/* Description -- editable when idle, shimmer while running */}
        <div className="mt-2 h-[60px]">
          {isRunning ? (
            <div className="space-y-1.5">
              <ShimmerPlaceholder width="100%" height={14} />
              <ShimmerPlaceholder width="80%" height={14} />
            </div>
          ) : (
            <textarea
              data-testid="character-description-input"
              aria-label={t('characterDesignerNode.characterDescription')}
              value={in_description || ''}
              placeholder={t('characterDesignerNode.describeCharacter')}
              onChange={handleDescriptionChange}
              className="w-full h-full text-sm text-muted-foreground leading-relaxed bg-transparent border-none outline-none p-0 m-0 resize-none nodrag nowheel"
              rows={3}
            />
          )}
        </div>
      </div>

      {/* Quick Info Bar -- Config | Voice | Variants. */}
      <QuickInfoBar
        presentation="designer"
        configSummary={configSummary}
        references={currentRefs}
        nodeStatus={status}
        primaryVoiceReference={
          hasLinkedVoice && displayVoiceName
            ? {
                nodeId: linkedVoiceNodeId ?? id,
                label: displayVoiceName,
                status: linkedVoiceStatus,
              }
            : {
                nodeId: id,
                label: t('quickInfoBar.addVoice', 'Add voice'),
                actionLabel: t('quickInfoBar.add', 'Add'),
              }
        }
        projectId={projectId}
        cacheBust={cacheBust}
        variantCount={historyEntries.length}
        accentColor={agentColor.active}
        logSource="character-designer"
        onOpenConfig={() => openPanelToTab('config')}
        onOpenVoiceReference={handleVoiceChipClick}
        onOpenVariants={() => openPanelToTab('variants')}
      />

      <input
        ref={voiceUploadInputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        data-testid="character-voice-upload-input"
        onChange={handleVoiceUploadFileSelected}
      />

      {voiceMenuOpen && (
        <div
          className="absolute bottom-16 right-6 z-50 w-44 rounded-lg border border-border bg-background p-1 shadow-xl"
          data-testid="character-voice-source-menu"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-xs font-medium text-foreground transition-colors hover:bg-surface-2"
            data-testid="character-voice-source-upload"
            onClick={openUploadVoice}
          >
            <Upload className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
            {t('quickInfoBar.upload', 'Upload')}
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-xs font-medium text-foreground transition-colors hover:bg-surface-2"
            data-testid="character-voice-source-ai-generate"
            onClick={openAiGenerateVoice}
          >
            <Sparkles className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
            {t('quickInfoBar.aiGenerate', 'AI-generate')}
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-xs font-medium text-foreground transition-colors hover:bg-surface-2"
            data-testid="character-voice-source-select"
            onClick={openVoiceSourceSelector}
          >
            <MousePointer2 className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
            {t('quickInfoBar.selectFromCanvas', 'Select from canvas')}
          </button>
        </div>
      )}

      <ReferenceSelector
        isOpen={voiceSelectorOpen}
        onClose={() => setVoiceSelectorOpen(false)}
        onSave={() => {}}
        mode="node-selector"
        excludeNodeId={id}
        canvasNodes={reactFlowNodes}
        maxReferences={1}
        currentNodeSelections={
          linkedVoiceNode && isCharacterVoiceSourceNode(linkedVoiceNode)
            ? [{
                nodeId: linkedVoiceNode.id,
                nodeType: linkedVoiceNode.type ?? '',
                label: readSelectedVoiceName(linkedVoiceNode) ?? linkedVoiceNode.id,
              }]
            : []
        }
        onSaveNodeSelections={handleVoiceSourceSelection}
        nodeSelectorFilter={(candidate) => {
          if (candidate.type === 'audio') return true
          if (candidate.type !== 'soundExpert') return false
          const owner = readSoundExpertVoiceSourceOwner(candidate)
          return owner === undefined || owner === id
        }}
        nodeSelectorTitle={t('quickInfoBar.selectVoiceSource', 'Select voice source')}
        nodeSelectorEmptyLabel={t('quickInfoBar.noVoiceSources', 'No Sound Expert or Audio Upload nodes found')}
        nodeSelectorNoSelectionLabel={t('quickInfoBar.noVoiceSourceSelected', 'No voice source selected')}
      />
    </DesignerNodeShell>
  )
})

CharacterDesignerNode.displayName = 'CharacterDesignerNode'
export default CharacterDesignerNode
